import { ApolloError } from '@apollo/client';

import {
  ContractAnalyticsReportQuery,
  useContractAnalyticsReportQuery,
} from 'core/graphql';

import { ContractAnalyticsGranularity } from './analyticsModel';
import { useEsignPermissions } from './useEsignPermissions';

/** ТЗ 0078 — аналитика договоров ERP (вкладка «Аналитика» раздела). */

export type IContractAnalytics = NonNullable<
  ContractAnalyticsReportQuery['contractAnalytics']
>;

export type IContractSellerStat = IContractAnalytics['sellers'][number];

export type IContractCourseStat = IContractAnalytics['courses'][number];

export interface IContractAnalyticsFilters {
  dateFrom: string | null;
  dateTo: string | null;
  sellerId: string | null;
  /** ТЗ 0083: кто нажал «Отправить». */
  sentById?: string | null;
  /** ТЗ 0083: статусы через запятую; пусто — все. */
  status?: string | null;
  /** `student` | `company`; пусто — оба. */
  kind: string | null;
  course: string | null;
  granularity: ContractAnalyticsGranularity;
}

export interface IUseContractAnalytics {
  data: IContractAnalytics | null;
  loading: boolean;
  /** Идёт перезапрос по новым фильтрам — прошлые цифры приглушаются. */
  fetching: boolean;
  error?: ApolloError;
}

/**
 * Считает всё бэкенд — смена фильтра это просто новый запрос. Пока ответ на
 * новые фильтры не пришёл, держим прошлый (`previousData`): смена переменных
 * иначе размонтировала бы графики на «Загрузка…» (грабли
 * `query-vars-change-unmounts-subtree`).
 *
 * Читают роли договоров студентов; без права запрос не уходит вовсе —
 * отказ прав отклонил бы промис поверх экрана.
 */
export const useContractAnalytics = (
  filters: IContractAnalyticsFilters,
  enabled = true,
): IUseContractAnalytics => {
  const { canReadStudent } = useEsignPermissions();
  const {
    data,
    previousData,
    loading,
    error,
  } = useContractAnalyticsReportQuery({
    variables: {
      dateFrom: filters.dateFrom || null,
      dateTo: filters.dateTo || null,
      sellerId: filters.sellerId || null,
      sentById: filters.sentById || null,
      status: filters.status || null,
      kind: filters.kind || null,
      course: filters.course || null,
      granularity: filters.granularity,
    },
    skip: !enabled || !canReadStudent,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const current = (data ?? previousData)?.contractAnalytics ?? null;
  return {
    data: current,
    loading: loading && !current,
    fetching: loading,
    error,
  };
};
